import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { navigation } from '../lib/navigation'
import { api } from '../lib/api'
import { useLiveConnection, useStateEvents } from '../lib/events'
import { money, calendarDate, daysUntil } from '../lib/format'
import { consoleUrl, isConsole } from '../lib/surface'
import ActingBanner from './ActingBanner'
import type { Wallet } from '../lib/types'
import { useToast } from '../lib/toast'
import { useApi } from '../lib/useApi'
import { useAuth } from '../lib/useAuth'
import { useTheme } from '../lib/theme'
import CommandPalette from './CommandPalette'
import { Tooltip } from './overlay'
import Shortcuts from './Shortcuts'
import Logo from './Logo'
import { Button } from './ui'
import {
  IconAccount,
  IconBilling,
  IconDatabase,
  IconHome,
  IconInbox,
  IconLedger,
  IconMenu,
  IconMoon,
  IconSearch,
  IconRocket,
  IconServices,
  IconShield,
  IconSun,
} from './icons'

/* The shortcut the search hint advertises. HIG platform adaptivity: a
   Windows keyboard has no ⌘, and showing one there is a small lie. */
const SEARCH_KEY = /mac/i.test(navigator.platform) ? '⌘K' : 'Ctrl K'

/** The letter shown when there is no GitHub picture to show.
 *
 * From the name if there is one, otherwise the email — which every account
 * has, so this never comes back empty. Uppercased here rather than in CSS so
 * it is the same letter for a screen reader and for the eye. */
function initial(user?: { name?: string | null; email?: string } | null): string {
  const source = user?.name?.trim() || user?.email?.trim() || ''
  return source.charAt(0).toUpperCase()
}

/** The one line the wallet chip gets to explain itself: what the account
 *  costs a month, and when that is next taken. */
function walletHint(wallet: Wallet): string {
  const cost = `${money(wallet.monthly_burn, wallet.currency)} a month for what you have running`
  const trial = daysUntil(wallet.trial_ends_at)
  if (trial !== null && trial > 0) {
    return `${cost} · free until ${calendarDate(wallet.trial_ends_at)}`
  }
  if (!wallet.next_charge_at) return `${cost} · nothing is billing yet`
  return `${cost} · next charge ${calendarDate(wallet.next_charge_at)}`
}

const NAV_ICONS: Record<string, typeof IconHome> = {
  '/': IconHome, '/projects': IconRocket, '/services': IconServices, '/databases': IconDatabase,
  '/protection': IconShield, '/insights': IconLedger, '/templates': IconRocket,
  '/experts': IconAccount, '/support': IconInbox, '/mail': IconInbox, '/billing': IconBilling,
  '/account': IconAccount, '/customers': IconAccount, '/analytics': IconLedger,
  '/deploys': IconRocket, '/invites': IconInbox, '/audit': IconShield,
}

export default function Layout() {
  const { user } = useAuth()
  const { theme, toggle } = useTheme()
  const [navOpen, setNavOpen] = useState(false)
  const sidebar = useRef<HTMLElement>(null)
  const main = useRef<HTMLElement>(null)
  const header = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!navOpen) return
    const previous = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const content = main.current
    content?.setAttribute('inert', '')
    const topbar = header.current
    topbar?.setAttribute('inert', '')
    const controls = () => Array.from(sidebar.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), [tabindex="0"]') ?? []).filter(el => el.getClientRects().length)
    controls()[0]?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setNavOpen(false) }
      if (event.key !== 'Tab') return
      const items = controls(), first = items[0], last = items.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    const onResize = () => { if (window.innerWidth > 900) setNavOpen(false) }
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => {
      document.body.style.overflow = overflow
      content?.removeAttribute('inert')
      topbar?.removeAttribute('inert')
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
      previous?.focus()
    }
  }, [navOpen])
  const location = useLocation()
  const navigate = useNavigate()
  const { notify } = useToast()
  const connected = useLiveConnection()

  // The wallet is the one number worth watching from every page.
  const { data: wallet, reload: reloadWallet } = useApi(() => api.wallet(), [], 60_000)

  // A deploy you started five minutes ago should find you wherever you are,
  // rather than waiting to be discovered on the services page. Only the two
  // outcomes are announced — the stages in between are noise unless you are
  // watching that build, and if you are, the timeline already shows them.
  useStateEvents((event) => {
    if (event.kind !== 'deploy') return
    const name = event.name ?? 'Your service'
    if (event.status === 'live') {
      notify(`${name} is live`, 'success')
      reloadWallet()
    } else if (event.status === 'failed') {
      notify(`${name} failed to deploy — ${event.message ?? 'see the build log'}`, 'error')
    }
  })

  // Vim-style jumps: `g` then a letter. Developers who live in one tab all day
  // reach for these; everyone else never presses `g` on its own and never
  // notices. The listener is armed only for a moment, so a stray `g` in normal
  // typing cannot swallow the next keystroke.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey) {
        const target = e.target as HTMLElement | null
        if (target?.isContentEditable) return
        if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
        const next = (event: KeyboardEvent) => {
          const to = navigation(isConsole).find(item => item.shortcut === event.key)?.to
          if (to) navigate(to)
          window.removeEventListener('keydown', next)
        }
        window.addEventListener('keydown', next)
        window.setTimeout(() => window.removeEventListener('keydown', next), 1_200)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  // A route change on mobile should close the slide-over.
  useEffect(() => setNavOpen(false), [location.pathname])

  const openPalette = () => {
    window.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }),
    )
  }

  return (
    <div className="shell" data-surface={isConsole ? 'admin' : 'app'}>
      {/* Above everything: whose account is on screen, when it is not the
          operator's own. */}
      <a className="skip-link" href="#main-content">Skip to content</a>
      <ActingBanner />
        <header className="workspace-header" ref={header} aria-label="TalariCloud workspace">
      <div className="header-navigation">
        <Button
          variant="ghost"
          className="icon header-menu"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation" aria-expanded={navOpen} aria-controls="workspace-navigation"
        >
          <IconMenu className="" size={18} />
        </Button>
        <div>
          <div className="brand stack" style={{ alignItems: 'flex-start', gap: 6 }}>
            <NavLink to="/" aria-label="TalariCloud home"><Logo size={26} /></NavLink>
            <span>
              <Tooltip
                placement="bottom"
                label={
                  connected
                    ? 'Connected. Deploys, builds and databases update the moment they change.'
                    : 'Not connected. Reconnecting — the page falls back to refreshing on a timer.'
                }
              >
                <span className="brand-region row" tabIndex={0} style={{ gap: 6 }}>
                  {isConsole ? 'Operator console' : 'et-addis-1'}
                  <span
                    className={connected ? 'live-dot' : 'live-dot off'}
                    aria-hidden="true"
                  />
                  <span className="sr-only">
                    {connected ? 'Live updates connected' : 'Live updates reconnecting'}
                  </span>
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
        <div className="header-account">


          <div className="row" style={{ gap: 6, justifyContent: 'space-between' }}>
            <NavLink to="/account" className="account-row" aria-label="Your profile and account">
              {user?.github_avatar_url ? (
                <img className="avatar" src={user.github_avatar_url} alt="" />
              ) : (
                /* An initial, not an empty disc. Signing up with an email
                   rather than GitHub left a blank grey circle here, which
                   reads as an avatar that failed to load rather than as a
                   deliberate placeholder. */
                <span className="avatar" aria-hidden="true">{initial(user)}</span>
              )}
              <span className="truncate tiny">{user?.name ?? user?.email}</span>
            </NavLink>


          </div>
          {!isConsole && (
            <>
              <Tooltip
                label={
                  wallet
                    ? walletHint(wallet)
                    : 'Your prepaid wallet balance'
                }
              >
                <NavLink
                  to="/billing"
                  className={`wallet-chip ${wallet?.low_balance ? 'low' : ''}`}
                >
                  <span className="tiny muted">Wallet</span>
                  <span className="wallet-amount tabular">
                    {wallet ? money(wallet.balance, wallet.currency) : '—'}
                  </span>
                </NavLink>
              </Tooltip>
            </>
          )}
        </div>
        </header>


      {navOpen && (
        <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}

      <aside id="workspace-navigation" ref={sidebar} className={`sidebar ${navOpen ? 'open' : ''}`} role={navOpen ? 'dialog' : undefined} aria-modal={navOpen || undefined} aria-label="Workspace navigation">
        {navOpen && <Button className="nav-close" onClick={() => setNavOpen(false)}>Close navigation <span aria-hidden="true">×</span></Button>}


        <Button
          variant="secondary"
          onClick={openPalette}
          style={{ justifyContent: 'space-between' }}
        >
          <span className="row" style={{ gap: 8 }}>
            <IconSearch className="" size={15} />
            <span className="muted">Search…</span>
          </span>
          <span className="kbd">{SEARCH_KEY}</span>
        </Button>

        <nav className="nav" aria-label="Main">
          {[...new Set(navigation(isConsole).map(item => item.group))].filter(group => group !== 'Settings').map(group => (
            <div className="nav-group" key={group}>
              <span className="nav-label">{group}</span>
              {navigation(isConsole).filter(item => item.group === group).map(({to, label}) => {
                const Icon = NAV_ICONS[to] ?? IconHome
                return <NavLink key={to} to={to} end={to === '/'}><Icon /><span>{label}</span></NavLink>
              })}
            </div>
          ))}
          {user?.is_admin && !isConsole && <a className="console-link" href={consoleUrl()}><IconShield /> Operator console <span aria-hidden="true">↗</span></a>}
        </nav>


        <div className="sidebar-foot">
            <Button
              variant="ghost"
              className="icon"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? (
                <IconSun className="" size={16} />
              ) : (
                <IconMoon className="" size={16} />
              )}
            </Button>
        </div>
      </aside>

      <main className="main" ref={main} id="main-content" tabIndex={-1}>

        <div className="page">
          {user?.is_admin && !user.two_factor_enabled && (
            <div className="alert warn">
              <strong>Operator accounts need two-factor authentication.</strong>{' '}
              The console stays locked until you turn it on in{' '}
              <NavLink to="/account">Account</NavLink>. It takes a minute with any
              authenticator app.
            </div>
          )}
          {user?.is_suspended && (
            <div className="alert error">
              <strong>This account is suspended.</strong>{' '}
              {user.suspension_reason ?? 'Contact support.'}
              <br />
              Your services and databases are stopped and nothing can be
              deployed. Your data is intact — you can still browse everything
              here, export your account and download your database backups.
              Reply to the email we sent to appeal.
            </div>
          )}
          <Outlet />
        </div>
      </main>

      <CommandPalette />
      <Shortcuts />
    </div>
  )
}
